import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import CyclesDisplay from './CyclesDisplay';

const PROGRESS_LAYER_ID = 'buddha-progress-layer';

function clampPercent(value: number): number {
  if (Number.isNaN(value)) return 0;
  return Math.min(Math.max(value, 0), 1);
}

function useProgressLayerRoot(): HTMLElement | null {
  const [node, setNode] = useState<HTMLElement | null>(() => {
    if (typeof document === 'undefined') return null;
    return document.getElementById(PROGRESS_LAYER_ID);
  });

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const el = document.getElementById(PROGRESS_LAYER_ID);
    setNode(el);
  }, []);

  return node;
}

function ProgressBarLayer({
  progressPercent,
}: {
  progressPercent: number;
}) {
  const layerNode = useProgressLayerRoot();
  const percentValue = clampPercent(progressPercent);
  const percentDisplay = Math.round(percentValue * 100);

  const gradientFill = (
    <div
      className="pointer-events-none absolute left-0 right-0 top-4 flex justify-center px-4 sm:top-5"
      style={{ paddingTop: 'max(env(safe-area-inset-top, 0px), 6px)' }}
    >
      <div className="flex w-full max-w-xl justify-center sm:max-w-2xl">
        <div className="w-40 sm:w-56 lg:w-72">
          <div className="relative h-3 w-full overflow-hidden rounded-full bg-white/10 ring-1 ring-white/5">
            <div
              className="absolute inset-0 rounded-full bg-white/5"
              aria-hidden="true"
            />
            <div
              className="h-full rounded-full transition-[width] duration-300 ease-out"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={percentDisplay}
              aria-label="Session progress"
              style={{
                width: `${percentDisplay}%`,
                background:
                  'linear-gradient(90deg, #3BBAC3 0%, #598ED1 35%, #7660DE 70%, #9333EA 100%)',
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );

  return layerNode ? createPortal(gradientFill, layerNode) : gradientFill;
}

interface ProgressOverlayProps {
  /** Successful cycles that should be displayed */
  cyclesCompleted: number;
  /** Progress toward the goal as a 0..1 value */
  progressPercent?: number;
  /** Optional pause toggle handler */
  onPauseToggle?: () => void;
  /** Whether the game is currently paused */
  isPaused?: boolean;
  /** Show the pause control (e.g., only when game is active) */
  showPause?: boolean;
  /** Session duration in seconds */
  durationSeconds?: number;
}

export default function ProgressOverlay({
  cyclesCompleted,
  progressPercent = 0,
  onPauseToggle,
  isPaused = false,
  showPause = false,
  durationSeconds = 0,
}: ProgressOverlayProps) {
  const percentValue = clampPercent(progressPercent);
  const percentDisplay = Math.round(percentValue * 100);

  return (
    <>
      <ProgressBarLayer progressPercent={percentValue} />
      <div
        className="pointer-events-none fixed left-0 right-0 top-4 z-40 flex justify-center px-4 sm:top-5"
        style={{ paddingTop: 'max(env(safe-area-inset-top, 0px), 6px)' }}
      >
        <div className="flex w-full max-w-xl flex-col items-center gap-2 sm:max-w-2xl sm:flex-row sm:justify-center sm:gap-3 md:justify-between">
          <div className="flex items-center gap-3">
            <CyclesDisplay
              count={cyclesCompleted}
              durationSeconds={durationSeconds}
            />

            <div className="w-40 sm:w-56 lg:w-72" aria-hidden="true">
              <div className="invisible h-3 w-full rounded-full bg-white/10 ring-1 ring-white/5" />
            </div>
            <div className="flex items-center gap-2">
              <div className="min-w-[36px] text-right text-xs font-semibold text-white/80">
                {percentDisplay}%
              </div>
              {onPauseToggle && showPause && (
                <button
                  type="button"
                  onClick={onPauseToggle}
                  aria-pressed={isPaused}
                  aria-label={isPaused ? 'Resume game' : 'Pause game'}
                  className="pointer-events-auto min-h-[40px] min-w-[72px] rounded-md bg-white/10 px-4 py-2 text-sm font-semibold text-white/90 shadow-sm hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/30"
                >
                  {isPaused ? 'Resume' : 'Pause'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
